import { Typography, Divider, Grid2 } from "@mui/material";
import { Box, Button, IconWithTooltip } from "@/components/atoms";
import { LoadingState, ErrorState } from "@/components/views";
import BookmarkIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkAddedIcon from "@mui/icons-material/Bookmark";
import { ButtonSet } from "../ButtonSet";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { useUserContext } from "@/providers/contexts";
import { ModalProps } from "./Modal";

export const ModalContent = ({
  componentName,
  children,
  header,
  subheader,
  error,
  loading,
  logoUrl,
  bookmarked,
  onBookmarkToggle,
  showBookmark = false,
  disableBookmark = false,
  onCloseButtonClick,
  renderButtonSet,
  handleRightButtonClick,
  rightButtonText = "Save",
  disableRightButton,
  disableButtonSet,
  handleFlagErrorClick,
  handleLeftButtonClick,
  isJobCard = false,
}: Omit<ModalProps, "open">) => {
  const { isAuthenticated } = useUserContext();

  if (error) return <ErrorState errors={error} />;
  if (loading) return <LoadingState />;

  if (isJobCard) {
    return (
      <Box componentName={`${componentName}-job-card-wrapper`} border={false}>
        {children}
        <Button
          componentName={`${componentName}-close`}
          sx={{ width: "100%", mt: 2 }}
          onClick={onCloseButtonClick}
        >
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box
      border={false}
      componentName={`${componentName}-content-wrapper`}
      sx={{ width: "100%", display: "flex" }}
      flexDirection="column"
      rowGap={2}
    >
      <Box
        componentName={`${componentName}-header`}
        sx={{
          border: "none",
          display: "flex",
          columnGap: "1rem",
          flexDirection: "row",
          textTransform: "capitalize",
          justifyContent: "space-between",
          alignContent: "center",
          width: "100%",
        }}
      >
        <Box
          componentName={`${componentName}-logo`}
          flexDirection="row"
          alignItems="center"
          justifyContent={logoUrl ? "flex-start" : "center"}
          sx={{
            display: "flex",
            columnGap: 4,
            border: "none",
            mx: 2,
          }}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${header}-logo`}
              style={{
                width: 80,
                height: 80,
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          )}
          <Typography variant="h3">{header}</Typography>
        </Box>

        {showBookmark && onBookmarkToggle && (
          <Grid2 container spacing={1.5}>
            <Grid2>
              <IconWithTooltip
                componentName="job-card-flag-icon"
                IconComponent={ReportProblemIcon}
                tooltipText="Report this job post if it is errored"
                onClick={handleFlagErrorClick}
                disabledContent={!isAuthenticated}
              />
            </Grid2>
            <Grid2>
              <IconWithTooltip
                componentName={`${componentName}-bookmark`}
                IconComponent={bookmarked ? BookmarkAddedIcon : BookmarkIcon}
                tooltipText={
                  bookmarked
                    ? "Remove this from bookmarks"
                    : "Save this to bookmarks"
                }
                onClick={onBookmarkToggle}
                disabledContent={disableBookmark}
              />
            </Grid2>
          </Grid2>
        )}
      </Box>

      {subheader && <Typography variant="h6">{subheader}</Typography>}

      {children && (
        <Box
          border={false}
          componentName={`${componentName}-children-wrapper`}
          flexDirection="column"
          alignItems="center"
          justifyContent={logoUrl ? "flex-start" : "center"}
          sx={{
            display: "flex",
            columnGap: 4,
            border: "none",
            mx: 2,
          }}
        >
          <Divider sx={{ width: "80%", mb: 2 }} color="#D3D3D3" />
          <Box
            componentName={`${componentName}-children`}
            border={false}
            sx={{
              width: "100%",
              maxHeight: "70%",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {children}
          </Box>
        </Box>
      )}

      {renderButtonSet ? (
        <ButtonSet
          componentName="account-details"
          leftBoxHandler={onCloseButtonClick || handleLeftButtonClick}
          rightBoxHandler={handleRightButtonClick}
          leftBoxText="Cancel"
          rightBoxText={rightButtonText}
          disableRightButton={loading || disableButtonSet || disableRightButton}
        />
      ) : (
        <Button
          componentName={`${componentName}-close`}
          sx={{ width: "100%", mt: "auto" }}
          onClick={onCloseButtonClick}
        >
          Close
        </Button>
      )}
    </Box>
  );
};
